import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AddressDialog from "../../lib/components/app/AddressDialog";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { useApi } from "../../lib/hooks/api/use-api";
import type { GetTypes } from "../../lib/types/api-types";
import Layout from "../layout";

export default function Addresses() {
	const { t } = useTranslation();
	const { users } = useApi();
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const { data: userAddresses, isLoading, refetch } = users.addresses.getAll();

	// Type for editing address state (props need explicit types)
	type Address = GetTypes["users"]["addresses"]["getAll"] extends {
		addresses?: infer Addrs;
	}
		? Addrs extends (infer Addr)[]
			? Addr
			: never
		: never;
	const [editingAddress, setEditingAddress] = useState<Address | null>(null);

	const handleDeleteAddress = (addressId: number) => {
		users.addresses.delete.mutate({ addressId: String(addressId) });
	};

	const openEditDialog = (address: Address) => {
		setEditingAddress(address);
	};

	const handleAddressSuccess = () => {
		// Refetch addresses after create/update
		refetch();
	};

	if (isLoading) {
		return (
			<Layout>
				<div className="p-8">
					<div className="max-w-4xl mx-auto">
						<div className="text-center">{t("profile.addresses.loading")}</div>
					</div>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div className="px-4 py-6 sm:px-6 lg:px-8 border min-h-full rounded-large bg-gradient-main">
				<div className="max-w-5xl mx-auto">
					{/* Header */}
					<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-10 sm:mb-12">
						<div className="space-y-2">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-primary/10 rounded-lg">
									<Icon name="MapPin" className="w-6 h-6 text-primary" />
								</div>
								<h1 className="text-3xl sm:text-4xl font-manrope font-bold text-foreground">
									{t("profile.addresses.header")}
								</h1>
							</div>
							<p className="text-muted-foreground text-base sm:text-lg">
								{t("profile.addresses.subheader")}
							</p>
						</div>
						<AddressDialog
							mode="create"
							open={isCreateDialogOpen}
							onOpenChange={setIsCreateDialogOpen}
							onSuccess={handleAddressSuccess}
							phoneRequired={true}
							trigger={
								<Button
									size="lg"
									variant="primary"
									className="w-full sm:w-auto"
								>
									<Icon name="Plus" className="w-5 h-5 mr-2" />
									{t("profile.addresses.add_new")}
								</Button>
							}
						/>
					</div>

					{/* Addresses List */}
					<div className="space-y-6">
						{userAddresses?.addresses?.length === 0 ? (
							<Card className="p-8 sm:p-12 text-center border-2 border-dashed border-muted-foreground/25 bg-muted/20">
								<div className="max-w-md mx-auto space-y-6">
									<div className="relative">
										<div className="w-20 h-20 bg-primary/10 rounded-full mx-auto flex items-center justify-center">
											<Icon name="MapPin" className="w-10 h-10 text-primary" />
										</div>
										<div className="absolute -top-1 -right-1 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
											<Icon
												name="Plus"
												className="w-3 h-3 text-primary-foreground"
											/>
										</div>
									</div>
									<div className="space-y-3">
										<h3 className="text-2xl font-manrope font-semibold text-foreground">
											{t("profile.addresses.no_addresses.header")}
										</h3>
										<p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
											{t("profile.addresses.no_addresses.subheader")}
										</p>
									</div>
									<Button
										onClick={() => setIsCreateDialogOpen(true)}
										size="lg"
										className="w-full sm:w-auto shadow-lg"
									>
										<Icon name="Plus" className="w-5 h-5 mr-2" />
										{t("profile.addresses.add_first")}
									</Button>
								</div>
							</Card>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
								<AnimatePresence mode="popLayout">
									{userAddresses?.addresses
										?.sort((a, b) => {
											// Default addresses always come first
											if (a.isDefault && !b.isDefault) return -1;
											if (!a.isDefault && b.isDefault) return 1;

											// Then sort by createdAt (most recent first)
											const aDate = new Date(a.createdAt || 0).getTime();
											const bDate = new Date(b.createdAt || 0).getTime();
											return bDate - aDate;
										})
										.map((address) => (
											<motion.div
												key={address.id}
												layout
												layoutId={address.id.toString()}
												initial={{ opacity: 0, y: 20 }}
												animate={{ opacity: 1, y: 0 }}
												exit={{ opacity: 0, y: -20 }}
												transition={{
													layout: { duration: 0.3, ease: "easeInOut" },
													opacity: { duration: 0.2 },
													y: { duration: 0.2 },
												}}
												className="group relative overflow-hidden border-0 shadow-lg bg-white rounded-lg"
											>
												{address.isDefault && (
													<div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 text-xs font-medium rounded-bl-lg">
														<Icon name="Star" className="w-3 h-3 inline mr-1" />
														{t("profile.addresses.default_badge")}
													</div>
												)}
												<div className="p-5 sm:p-6">
													<div className="flex items-start gap-4">
														<div className="shrink-0">
															<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
																<Icon
																	name="MapPin"
																	className="w-6 h-6 text-primary"
																/>
															</div>
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-2 mb-3">
																<h3 className="text-lg font-manrope font-semibold text-foreground truncate">
																	{address.label}
																</h3>
															</div>

															<div className="space-y-2 text-muted-foreground">
																<div className="flex items-start gap-2">
																	<Icon
																		name="House"
																		className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/60"
																	/>
																	<div className="text-sm leading-relaxed">
																		<p className="font-medium text-foreground">
																			{address.streetAddress}
																		</p>
																		<p>
																			{address.city}, {address.state}{" "}
																			{address.zipCode}
																		</p>
																		{address.country !== "USA" && (
																			<p>{address.country}</p>
																		)}
																	</div>
																</div>

																{address.phone && (
																	<div className="flex items-center gap-2">
																		<Icon
																			name="Phone"
																			className="w-4 h-4 shrink-0 text-muted-foreground/60"
																		/>
																		<p className="text-sm">{address.phone}</p>
																	</div>
																)}

																{address.instructions && (
																	<div className="flex items-start gap-2">
																		<Icon
																			name="FileText"
																			className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/60"
																		/>
																		<p className="text-sm italic">
																			{address.instructions}
																		</p>
																	</div>
																)}
															</div>
														</div>
													</div>

													<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-6 pt-4 border-t border-border/50">
														<Button
															variant="primary"
															size="sm"
															onClick={() => openEditDialog(address)}
														>
															<Icon name="Pencil" className="w-4 h-4 mr-2" />
															{t("profile.addresses.edit")}
														</Button>
														<Button
															variant="outline"
															size="sm"
															onClick={() => handleDeleteAddress(address.id)}
															disabled={users.addresses.delete.isPending}
														>
															<Icon name="Trash2" className="w-4 h-4 mr-2" />
															{t("profile.addresses.delete")}
														</Button>
													</div>
												</div>
											</motion.div>
										))}
								</AnimatePresence>
							</div>
						)}
					</div>

					{/* Edit Dialog */}
					<AddressDialog
						mode="edit"
						open={!!editingAddress}
						onOpenChange={(open) => !open && setEditingAddress(null)}
						address={editingAddress}
						onSuccess={() => {
							handleAddressSuccess();
							setEditingAddress(null);
						}}
						phoneRequired={true}
					/>
				</div>
			</div>
		</Layout>
	);
}
